<template>
    <div class="pb-5 pt-5 instance">
        <div>
            <div v-if="error" class="alert alert-danger">
                {{ error }}
            </div>
            <div v-else>
                <div class="row m-0">
                    <div class="col-12">
                        <div class="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center pb-2 pt-2 mt-1 mb-1">
                            <h5 class="title mt-2">Paiement</h5>
                        </div>
                    </div>
                </div>

                <div v-if="formErrors.length" class="alert alert-danger">
                    <p v-for="(msg, idx) in formErrors" :key="idx" class="mb-1">{{ msg }}</p>
                </div>

                <LoaderComponent v-if="isFormLoading" />

                <form v-else @submit.prevent="saveSettings" method="post">
                    <div class="row m-0">
                        <div class="col-12">
                            <div class="border-light b-radius-20 p-4 mb-3 payment-panel">
                                <div class="row mb-3">
                                    <div class="col-12">
                                        <strong>Paramètres de paiement</strong>
                                    </div>
                                </div>

                                <div class="payment-intro">
                                    <div class="payment-intro__title">Par défaut, aucun paiement n'est demandé dans le widget.</div>
                                    <div class="payment-intro__subtitle">
                                        Activez le parcours de paiement uniquement si vous souhaitez faire choisir un mode de règlement au client.
                                    </div>
                                </div>

                                <switch-input-component v-model="paymentRequired" :disabled="!has_right_to_update">
                                    <template v-slot:label>
                                        <div>Activer le paiement</div>
                                        <small class="text-muted">
                                            Si désactivé, la commande est envoyée sans étape de paiement dans le widget.
                                        </small>
                                    </template>
                                </switch-input-component>

                                <div class="mt-4 mb-0">
                                    <label class="form-label d-block payment-methods-label">Modes de paiement proposés</label>
                                    <div v-if="paymentRequired" class="payment-provider-note">
                                        <strong>Paiement en ligne</strong>
                                        <span>Seule l'intégration Stripe est disponible aujourd'hui pour l'encaissement par carte bancaire.</span>
                                    </div>
                                    <div class="payment-method-grid">
                                        <button
                                            v-for="method in paymentMethodOptions"
                                            :key="method.value"
                                            type="button"
                                            class="payment-method-card"
                                            :class="{
                                                active: isPaymentMethodSelected(method.value),
                                                disabled: !paymentRequired || !has_right_to_update,
                                            }"
                                            :disabled="!has_right_to_update || !paymentRequired"
                                            @click="togglePaymentMethod(method.value)">
                                            <span class="card-check" aria-hidden="true">
                                                <span v-if="isPaymentMethodSelected(method.value)" class="card-check__dot"></span>
                                            </span>
                                            <div class="provider-meta">
                                                <div class="provider-title">
                                                    {{ method.label }}
                                                    <span v-if="method.badge" class="provider-badge">{{ method.badge }}</span>
                                                </div>
                                                <div class="provider-subtitle">{{ method.description }}</div>
                                                <div v-if="method.hint" class="provider-hint">{{ method.hint }}</div>
                                            </div>
                                        </button>
                                    </div>
                                    <small v-if="!paymentRequired" class="text-muted d-block mt-2">
                                        Le widget reste en prise de commande simple tant que le paiement n'est pas activé.
                                    </small>
                                    <small v-else-if="!paymentMethods.length" class="text-danger d-block mt-2">
                                        Sélectionnez au moins un mode de paiement pour pouvoir enregistrer.
                                    </small>
                                </div>

                                <div v-if="selectedPaymentLabels.length" class="selected-methods">
                                    <div class="selected-methods__label">Paiements sélectionnés</div>
                                    <div class="selected-methods__list">
                                        <span v-for="label in selectedPaymentLabels" :key="label" class="selected-method-pill">
                                            {{ label }}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="row m-0 mb-4">
                        <div class="col-12">
                            <button :disabled="!has_right_to_update || saveDisabled" type="submit" name="submit" class="btn btn-success btn-circle">
                                Enregistrer
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    </div>
</template>

<script>
import LoaderComponent from "../../../components/LoaderComponent.vue";
import SwitchInputComponent from "../../../components/forms/SwitchInputComponent.vue";

export default {
    components: {
        LoaderComponent,
        SwitchInputComponent,
    },
    data() {
        return {
            isFormLoading: false,
            error: null,
            formErrors: [],
            paymentRequired: false,
            savedPaymentRequired: false,
            paymentMethods: [],
            savedPaymentMethods: [],
        };
    },
    computed: {
        rights() {
            return this.$store.getters["users/formattedRights"];
        },
        has_right_to_update() {
            return this.rights.includes("click_and_collect.config.update");
        },
        restaurant_id() {
            return Number.parseInt(this.$route.params.restaurant_id);
        },
        paymentMethodOptions() {
            return [
                {
                    value: "card",
                    label: "Stripe",
                    badge: "CB",
                    description: "Paiement en ligne par carte bancaire via Stripe.",
                    hint: "Le client règle en ligne dans le widget avec Stripe.",
                },
                {
                    value: "counter",
                    label: "Paiement au comptoir",
                    description: "Le client règle directement sur place.",
                    hint: null,
                },
            ];
        },
        saveDisabled() {
            return this.paymentRequired && this.paymentMethods.length === 0;
        },
        selectedPaymentLabels() {
            return this.paymentMethodOptions.filter((method) => this.paymentMethods.includes(method.value)).map((method) => method.label);
        },
    },
    created() {
        this.fetchSettings();
    },
    methods: {
        fetchSettings() {
            this.isFormLoading = true;
            this.httpGet(`/api/ordering-table/${this.restaurant_id}/payment-settings`)
                .then((response) => {
                    const value = response?.data?.data?.payment_required;
                    this.paymentRequired = typeof value === "boolean" ? value : false;

                    const methods = response?.data?.data?.payment_methods;
                    if (Array.isArray(methods)) {
                        this.paymentMethods = methods.filter((method) => ["card", "counter"].includes(method));
                    } else {
                        const provider = response?.data?.data?.payment_provider;
                        this.paymentMethods = provider === "stripe" ? ["card"] : [];
                    }

                    this.savedPaymentRequired = this.paymentRequired;
                    this.savedPaymentMethods = [...this.paymentMethods];
                })
                .finally(() => (this.isFormLoading = false));
        },
        saveSettings() {
            this.formErrors = [];

            if (this.paymentRequired && this.paymentMethods.length === 0) {
                this.formErrors = ["Veuillez sélectionner au moins un mode de paiement."];
                return;
            }

            this.isFormLoading = true;
            const previousRequired = this.savedPaymentRequired;
            const previousMethods = [...this.savedPaymentMethods];

            this.httpPost(`/api/ordering-table/${this.restaurant_id}/payment-settings`, {
                payment_required: this.paymentRequired,
                payment_methods: this.paymentMethods,
            })
                .then((response) => {
                    if (response === false) {
                        this.paymentRequired = previousRequired;
                        this.paymentMethods = [...previousMethods];
                        return;
                    }

                    this.savedPaymentRequired = this.paymentRequired;
                    this.savedPaymentMethods = [...this.paymentMethods];
                })
                .catch((error) => {
                    const errors = error?.response?.data?.errors;
                    if (errors && typeof errors === "object") {
                        this.formErrors = Object.values(errors).flat();
                    } else if (error?.response?.data?.message) {
                        this.formErrors = [error.response.data.message];
                    } else {
                        this.formErrors = ["Impossible d'enregistrer les paramètres de paiement."];
                    }
                })
                .finally(() => (this.isFormLoading = false));
        },
        isPaymentMethodSelected(value) {
            return this.paymentMethods.includes(value);
        },
        togglePaymentMethod(value) {
            if (!this.has_right_to_update || !this.paymentRequired) {
                return;
            }

            if (this.isPaymentMethodSelected(value)) {
                this.paymentMethods = this.paymentMethods.filter((method) => method !== value);
                return;
            }

            this.paymentMethods = [...this.paymentMethods, value];
        },
    },
};
</script>

<style scoped>
.payment-panel {
    background: linear-gradient(180deg, rgba(8, 62, 96, 0.02), rgba(8, 62, 96, 0));
}

.payment-intro {
    padding: 14px 16px;
    border-radius: 16px;
    margin-bottom: 18px;
    background: rgba(8, 62, 96, 0.06);
    border: 1px solid rgba(8, 62, 96, 0.08);
}

.payment-intro__title {
    font-weight: 600;
    color: #083e60;
    margin-bottom: 4px;
}

.payment-intro__subtitle {
    font-size: 12px;
    color: #4b5563;
}

.payment-methods-label {
    margin-bottom: 10px;
    font-weight: 600;
}

.payment-method-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    gap: 12px;
    width: min(100%, 540px);
}

.payment-provider-note {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 12px 14px;
    border-radius: 14px;
    margin-bottom: 12px;
    background: rgba(8, 62, 96, 0.04);
    border: 1px solid rgba(8, 62, 96, 0.1);
    color: #083e60;
    font-size: 12px;
    width: min(100%, 540px);
}

.payment-method-card {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 16px;
    border-radius: 16px;
    border: 1px solid #d9e1e7;
    background: #ffffff;
    text-align: left;
    transition: all 0.18s ease;
}

.payment-method-card:hover:not(.disabled) {
    border-color: rgba(8, 62, 96, 0.28);
    box-shadow: 0 10px 24px rgba(8, 62, 96, 0.08);
}

.payment-method-card.active {
    border-color: #083e60;
    box-shadow: 0 0 0 2px rgba(8, 62, 96, 0.12);
    background: rgba(8, 62, 96, 0.03);
}

.payment-method-card.disabled {
    opacity: 0.6;
    cursor: not-allowed;
    box-shadow: none;
}

.card-check {
    width: 18px;
    height: 18px;
    border-radius: 999px;
    border: 2px solid #9ca3af;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.card-check__dot {
    width: 8px;
    height: 8px;
    border-radius: 999px;
    background: #083e60;
}

.provider-meta {
    flex: 1;
}

.provider-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-weight: 600;
    color: #0f172a;
    font-size: 14px;
}

.provider-subtitle {
    margin-top: 4px;
    font-size: 12px;
    color: #4b5563;
}

.provider-hint {
    margin-top: 6px;
    font-size: 11px;
    color: #6b7280;
}

.provider-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 20px;
    padding: 0 8px;
    border-radius: 999px;
    background: rgba(8, 62, 96, 0.08);
    color: #083e60;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
}

.selected-methods {
    margin-top: 18px;
}

.selected-methods__label {
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #6b7280;
    margin-bottom: 8px;
}

.selected-methods__list {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.selected-method-pill {
    padding: 6px 10px;
    border-radius: 999px;
    background: rgba(8, 62, 96, 0.08);
    color: #083e60;
    font-size: 12px;
    font-weight: 600;
}
</style>
